import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { IconButton } from '@/components/ds-zta';
import { usePermissions } from '@/hooks/usePermissions';
import { cn } from '@/lib/cn';
import { redactSecrets } from '@/lib/redact';

/* Recorded command lines and command output, with known secret patterns
 * redacted (ZTA design evaluation F13, decision D4 = A: masked by default).
 *
 * Display-level only (see lib/redact.ts): the API still returns the raw text.
 * An admin gets a per-row reveal, the same gate MaskedValue uses, since ZTA's
 * permission catalog has no reveal permission yet. D4 also asks for each
 * reveal to be audited; there is no endpoint to write that entry from the
 * console, so `onReveal` is the hook for it and nothing claims it is logged. */

export interface RedactedTextProps {
    value: string;
    /** What the text is, for the button's name: "command" → "Reveal command". */
    label: string;
    /** Classes for the <pre>. */
    className?: string;
    onReveal?: (value: string) => void;
}

export default function RedactedText({ value, label, className, onReveal }: RedactedTextProps) {
    const mayReveal = usePermissions().isAdmin();
    const [revealed, setRevealed] = useState(false);
    const redacted = redactSecrets(value);
    const isRedacted = redacted !== value;
    const showRaw = revealed && mayReveal;

    // A new value starts redacted again (reset during render, React's
    // "adjust state when a prop changes" pattern).
    const [prevValue, setPrevValue] = useState(value);
    if (prevValue !== value) {
        setPrevValue(value);
        setRevealed(false);
    }

    return (
        <div className="flex items-start gap-1" data-redacted={isRedacted && !showRaw ? 'true' : undefined}>
            <pre className={cn('min-w-0 flex-1', className)}>
                {showRaw ? value : redacted}
            </pre>
            {isRedacted && !showRaw && <span className="sr-only">(secrets redacted)</span>}
            {isRedacted && mayReveal && (
                <IconButton
                    size="sm"
                    aria-label={`${revealed ? 'Hide' : 'Reveal'} ${label}`}
                    aria-pressed={revealed}
                    icon={revealed ? <EyeOff className="h-3.5 w-3.5" aria-hidden /> : <Eye className="h-3.5 w-3.5" aria-hidden />}
                    onClick={() => setRevealed((r) => { if (!r) onReveal?.(value); return !r; })}
                />
            )}
        </div>
    );
}
